// MIRRORED FILE — twin of src/utils/clientInvoiceDispatch.js (line-for-line).
// CRA's ModuleScopePlugin forbids importing from the repo's src/, so the copy
// is the only way the page can use the rule. The pair is registered in
// `mirroredUtilParity.test.js` (PAIRS).
//
// How much of a client-invoice line still has to be dispatched — the one owner
// (BUSINESS_RULES#client-invoice-dispatch-remaining). Pure.
//
// remaining = max(0, invoiceQuantity - dispatchedQuantity), the invoiced
// quantity falling back to the confirmed one exactly as the dispatch summary and
// the delivery slip always read it. A line with nothing invoiced has nothing to
// dispatch; over-dispatch is never a negative remainder. Parsed with invoiceLineMath's
// `num` (the mirrored parser, blank = null) (a blank or unreadable invoiced figure falls through to the
// confirmed one, then to 0; an unreadable dispatched figure is 0 — nothing on
// record, so the line is still warned about) and rounded on the quantity grid:
// 0.7 + 0.1 dispatched is 0.7999999999999999, and 0.8 minus that must be 0.
import { num, roundQty } from './invoiceLineMath';

type DispatchLine = {
  invoiceQuantity?: unknown;
  confirmedQuantity?: unknown;
  dispatchedQuantity?: unknown;
  lineNumber?: unknown;
} | null | undefined;

// Item 18 (9 Oct QA retest) — the credit-note shape this file reads: only
// `status`, `reasonKey` and each item's `sourceInvoiceLineNumber`/`quantity`.
type CreditNoteForDispatch = {
  status?: unknown;
  reasonKey?: unknown;
  items?: ReadonlyArray<{ sourceInvoiceLineNumber?: unknown; quantity?: unknown }> | unknown;
} | null | undefined;

/**
 * `creditedShortQty` (Item 18): how much of this line a SHORT-DELIVERY credit
 * note already took off (a caller with credit notes in hand reads it with
 * `shortDeliveryCreditedQtyByLine`, below, and passes it in — this stays pure).
 * Default 0 keeps every existing caller that does not pass it reading exactly as
 * before.
 */
export function remainingDispatchQty(item: DispatchLine, creditedShortQty: unknown = 0): number {
  const dispatched = Math.max(0, num(item?.dispatchedQuantity) ?? 0);
  const creditedShort = Math.max(0, num(creditedShortQty) ?? 0);
  return Math.max(0, roundQty(invoicedQty(item) - dispatched - creditedShort));
}

/** What a line has invoiced, as dispatch reads it: invoiceQuantity, else confirmedQuantity, else 0; never below 0. */
export function invoicedQty(item: DispatchLine): number {
  return Math.max(0, num(item?.invoiceQuantity) ?? num(item?.confirmedQuantity) ?? 0);
}

/**
 * D157.6: a slip of `dispatchQty` (a number its caller parsed; below 0 is nothing) on this line. What it ships past
 * `remainingBefore` is `extra`, and the line then bills `billedAfter` = invoiced + extra, on the quantity grid. The
 * one sum the slip and the Delivery tab both read.
 */
export function billedAfterDispatch(
  item: DispatchLine,
  dispatchQty: number,
  creditedShortQty: unknown = 0
): { remainingBefore: number; extra: number; billedAfter: number } {
  const remainingBefore = remainingDispatchQty(item, creditedShortQty);
  const extra = Math.max(0, roundQty(Math.max(0, dispatchQty) - remainingBefore));
  return { remainingBefore, extra, billedAfter: roundQty(invoicedQty(item) + extra) };
}

/** The lines that have shipped anything, as { lineNumber, itemName, dispatched } (D96.3 cancel refusal). */
export function dispatchedLines(items: unknown): { lineNumber: number; itemName: string; dispatched: number }[] {
  return (Array.isArray(items) ? items : [])
    .map((it: DispatchLine & { lineNumber?: unknown; itemName?: unknown }, i: number) => ({
      lineNumber: num(it?.lineNumber) ?? i + 1,
      itemName: String(it?.itemName ?? ''),
      dispatched: Math.max(0, num(it?.dispatchedQuantity) ?? 0),
    }))
    .filter((l) => l.dispatched > 0);
}

/**
 * Lines with any quantity still to dispatch (a partial dispatch counts).
 * `creditedShortByLine` (Item 18): a `Map<string, number>` keyed by line number
 * (`shortDeliveryCreditedQtyByLine`, below) — optional, so an existing caller with
 * no credit notes in hand is unchanged.
 */
export function countUndispatchedLines(items: unknown, creditedShortByLine: Map<string, number> | null = null): number {
  return (Array.isArray(items) ? items : []).filter((it: DispatchLine, idx: number) => {
    const key = String((it as DispatchLine)?.lineNumber ?? idx + 1);
    const credited = creditedShortByLine ? creditedShortByLine.get(key) || 0 : 0;
    return remainingDispatchQty(it, credited) > 0;
  }).length;
}

/**
 * Item 18 (9 Oct QA retest) — how much of each invoice line a SHORT-DELIVERY
 * credit note has already taken off what is still owed as a dispatch. See the
 * server twin for the full rule; kept word-for-word identical here.
 *
 * Only a credit note whose REASON is `short_delivery` counts (the built-in key
 * `creditReason.service.js` lists — BUSINESS_RULES#credit-reason); only a
 * non-cancelled one (the same "non-cancelled" definition
 * `creditNote.service.listForInvoice` / `getCreditableLines` already use).
 * Grouped by `sourceInvoiceLineNumber`, magnitude only. Distinct question from
 * `creditedByLine` server-side (every reason, quantity AND money, for the
 * credit-note-authoring form) — this is quantity only, short-delivery only, for
 * "is there still a dispatch owed".
 */
export function shortDeliveryCreditedQtyByLine(creditNotes: unknown): Map<string, number> {
  const byLine = new Map<string, number>();
  for (const cn of Array.isArray(creditNotes) ? (creditNotes as CreditNoteForDispatch[]) : []) {
    if (!cn || cn.status === 'cancelled' || cn.reasonKey !== 'short_delivery') continue;
    for (const it of Array.isArray(cn.items) ? cn.items : []) {
      const ref = (it as { sourceInvoiceLineNumber?: unknown })?.sourceInvoiceLineNumber;
      if (ref === null || ref === undefined || ref === '') continue;
      const key = String(ref);
      const qty = Math.abs(num((it as { quantity?: unknown })?.quantity) ?? 0);
      byLine.set(key, (byLine.get(key) || 0) + qty);
    }
  }
  return byLine;
}

export type SlipRowForLine = { slipNumber: string | null; sequenceLabel: string | null; createdAt: string | null; quantity: number; unit: string | null };

/**
 * D158.3: what each delivery slip shipped of one line, in slip order, quantity in that slip's own unit. An exchange slip
 * names no invoice line (its rows number the swaps), so it is skipped. A line's rows add up to its `dispatchedQuantity`.
 */
export function slipRowsForLine(slips: unknown, lineNumber: unknown): SlipRowForLine[] {
  const line = num(lineNumber);
  const rows: SlipRowForLine[] = [];
  if (line === null) return rows;
  for (const slip of Array.isArray(slips) ? slips : []) {
    if (!slip || slip.isExchangeSlip) continue;
    for (const row of Array.isArray(slip.items) ? slip.items : []) {
      const quantity = num(row?.quantity);
      if (quantity === null || num(row?.lineNumber) !== line) continue;
      rows.push({ slipNumber: slip.slipNumber ?? null, sequenceLabel: slip.sequenceLabel ?? null, createdAt: slip.createdAt ?? null, quantity, unit: row.unit ?? null });
    }
  }
  return rows;
}
